import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, CheckCircle2, AlertTriangle, Loader2, Check, Copy, Download,
  Sparkles, BookOpen, GraduationCap, AlertCircle,
} from "lucide-react";
import {
  api,
  type AdminCourseDetailResponse,
  type AdminCourseModuleItem,
  type AdminCourseExamItem,
  type AdminCourseOrderItem,
  type University,
} from "../../lib/api";
import { useAdminToken } from "./adminShared";
import { validateImportJson, type ValidationResult } from "../../lib/extractorPrompt";

export function AdminManualExtractionPage() {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";
  const token = useAdminToken();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminCourseDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !courseId) return;
    api.admin.getCourseDetail(token, courseId)
      .then(setData)
      .catch((err: any) => setError(err?.message || "Course not found."));
  }, [token, courseId]);

  if (!courseId) {
    return <div className="p-6 text-sm text-destructive">A course must be selected before opening manual extraction.</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        {error}
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-muted-foreground">Loading manual extraction...</div>;
  }

  const { course, modules, exams, orders } = data;
  return (
    <AdminManualExtractionContent
      courseId={course.id}
      courseName={course.name}
      courseCode={course.code || ""}
      courseType={course.courseType || (course as any).course_type || "LANGUAGE"}
      extractionProfile={course.extractionProfile || (course as any).extraction_profile || null}
      modules={modules}
      exams={exams}
      orders={orders}
      onClose={() => navigate(`/admin/courses/${course.id}`)}
      onSuccess={() => navigate(`/admin/courses/${course.id}`)}
    />
  );
}

export function AdminManualExtractionContent({
  courseId,
  courseName,
  courseCode,
  courseType,
  extractionProfile,
  modules,
  exams,
  orders,
  onClose,
  onSuccess,
}: {
  courseId: string;
  courseName: string;
  courseCode: string;
  courseType?: string;
  extractionProfile?: string | null;
  modules: AdminCourseModuleItem[];
  exams: AdminCourseExamItem[];
  orders: AdminCourseOrderItem[];
  onClose?: () => void;
  onSuccess?: () => void;
}) {
  const token = useAdminToken();

  const [selectedMode, setSelectedMode] = useState<"NOTES" | "QUIZ" | "BOTH" | "PAST_EXAM">("QUIZ");
  const formMode = selectedMode === "PAST_EXAM" ? "past_exam" : "module";

  // Catalog Universities for Past Exam form
  const [universities, setUniversities] = useState<University[]>([]);
  useEffect(() => {
    api.universities().then((res) => {
      if (Array.isArray(res)) setUniversities(res);
    }).catch(() => {});
  }, []);

  const activeProfileUsed = useMemo(() => {
    if (extractionProfile && extractionProfile.trim()) return "Custom profile";
    const cType = (courseType || "LANGUAGE").toUpperCase();
    return `Default: ${cType}`;
  }, [extractionProfile, courseType]);

  // Form 1 State: Module Content (Quiz & Notes)
  const [selectedModuleId, setSelectedModuleId] = useState<string>(modules[0]?.id || "");
  const [selectedChapterId, setSelectedChapterId] = useState<string>("");
  const modulePreference = selectedMode === "NOTES" ? "notes" : selectedMode === "BOTH" ? "both" : "quiz";
  const [questionsPerLevel, setQuestionsPerLevel] = useState<number>(25);

  const availableChapters = useMemo(() => {
    const matchedOrder = orders.find((o) => o.moduleId === selectedModuleId);
    return matchedOrder?.chapters || [];
  }, [orders, selectedModuleId]);

  // Form 2 State: Past Exam
  const [selectedUniId, setSelectedUniId] = useState<string>("");
  const [examYear, setExamYear] = useState<string>("");
  const [examTerm, setExamTerm] = useState<"mid" | "final">("final");
  const [examTitleCustom, setExamTitleCustom] = useState<string>("");
  const [fromQ, setFromQ] = useState<string>("");
  const [toQ, setToQ] = useState<string>("");
  const [selectedExamResourceId, setSelectedExamResourceId] = useState<string>("");

  const computedExamTitle = useMemo(() => {
    const uni = universities.find((u) => u.id === selectedUniId);
    const uniLabel = uni ? (uni.short_name || uni.name) : "National";
    const termLabel = examTerm === "mid" ? "Midterm" : "Final";
    const y = examYear.trim() ? `${examYear.trim()} ` : "";
    return `${uniLabel} ${y}${termLabel} Exam`;
  }, [universities, selectedUniId, examYear, examTerm]);

  // Template generation state (shared across tabs)
  const [fetchingTemplate, setFetchingTemplate] = useState(false);
  const [templateData, setTemplateData] = useState<{
    template: string;
    filename: string;
    charCount: number;
    hasSourceText: boolean;
    profileUsed?: string;
  } | null>(null);
  const [templateCopied, setTemplateCopied] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);

  // Ingest state
  const [ingestMode, setIngestMode] = useState<"paste" | "file">("paste");
  const [pasteText, setPasteText] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState<string>("");
  const [customItemTitle, setCustomItemTitle] = useState("");
  const [targetStatus, setTargetStatus] = useState<"draft" | "active">("draft");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  // Read uploaded file text for validation
  useEffect(() => {
    if (ingestMode === "file" && uploadFile) {
      uploadFile.text().then((txt) => {
        setFileText(txt);
      }).catch(() => setFileText(""));
    } else {
      setFileText("");
    }
  }, [ingestMode, uploadFile]);

  // Live import validation
  const activeInputText = ingestMode === "paste" ? pasteText : fileText;
  const currentPreference = formMode === "module" ? modulePreference : "past_exam";
  const validation = useMemo<ValidationResult | null>(() => {
    if (currentPreference !== "notes" && activeInputText.trim()) {
      return validateImportJson(activeInputText);
    }
    return null;
  }, [currentPreference, activeInputText]);

  const handleFetchTemplate = async () => {
    if (!token) return;
    setErrorMsg(null);
    setFetchingTemplate(true);
    setTemplateCopied(false);

    try {
      if (formMode === "module") {
        const res = await (api.admin.courses?.getManualExtractionTemplate || api.admin.getManualExtractionTemplate)(token, courseId, {
          resourceId: selectedModuleId || undefined,
          chapterId: selectedChapterId || undefined,
          preference: selectedMode,
          questionsPerLevel: modulePreference !== "notes" ? (Number(questionsPerLevel) || 25) : undefined,
        });
        setTemplateData(res);
      } else {
        const effectiveTitle = examTitleCustom.trim() || computedExamTitle;
        const res = await (api.admin.courses?.getManualExtractionTemplate || api.admin.getManualExtractionTemplate)(token, courseId, {
          resourceId: selectedExamResourceId || undefined,
          preference: selectedMode,
          examName: effectiveTitle,
          universityId: selectedUniId || undefined,
          examYear: examYear.trim() || undefined,
          examTerm: examTerm,
          fromQ: fromQ ? Number(fromQ) : undefined,
          toQ: toQ ? Number(toQ) : undefined,
        });
        setTemplateData(res);
      }
      setPreviewExpanded(false);
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to generate extraction template");
    } finally {
      setFetchingTemplate(false);
    }
  };

  const handleCopyTemplate = async () => {
    if (!templateData?.template) return;
    try {
      await navigator.clipboard.writeText(templateData.template);
      setTemplateCopied(true);
      setTimeout(() => setTemplateCopied(false), 2500);
    } catch {
      setErrorMsg("Failed to copy to clipboard.");
    }
  };

  const handleDownloadTemplate = () => {
    if (!templateData?.template) return;
    const blob = new Blob([templateData.template], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = templateData.filename || `template_${formMode === "module" ? modulePreference : "past_exam"}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleIngestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setErrorMsg(null);
    setSuccessNotice(null);

    const hasText = ingestMode === "paste" && pasteText.trim().length > 0;
    const hasFile = ingestMode === "file" && uploadFile !== null;

    if (!hasText && !hasFile) {
      setErrorMsg(ingestMode === "paste" ? "Please paste the AI output." : "Please select an output file to upload.");
      return;
    }

    if (currentPreference !== "notes" && validation && !validation.valid) {
      setErrorMsg(`Cannot ingest: Please resolve the ${validation.errors.length} validation error(s) first.`);
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();

      if (formMode === "module") {
        fd.append("preference", modulePreference);
        if (selectedModuleId) fd.append("resource_id", selectedModuleId);
        if (selectedChapterId) fd.append("chapter_id", selectedChapterId);
        if (customItemTitle.trim()) fd.append("title", customItemTitle.trim());
      } else {
        fd.append("preference", "past_exam");
        if (selectedExamResourceId) fd.append("resource_id", selectedExamResourceId);
        if (selectedUniId) fd.append("university_id", selectedUniId);
        if (examYear.trim()) fd.append("exam_year", examYear.trim());
        fd.append("exam_class", examTerm);
        fd.append("exam_term", examTerm);
        const effectiveTitle = customItemTitle.trim() || examTitleCustom.trim() || computedExamTitle;
        fd.append("title", effectiveTitle);
      }

      fd.append("status", targetStatus);

      if (ingestMode === "paste") {
        fd.append("raw_text", pasteText.trim());
      } else if (uploadFile) {
        fd.append("file", uploadFile);
      }

      const res = await (api.admin.courses?.submitManualIngest || api.admin.submitManualIngest)(token, courseId, fd);
      setSuccessNotice(res.message || "Material successfully ingested!");
      setTimeout(() => {
        onSuccess?.();
      }, 1100);
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to ingest output. Please check the JSON or Markdown syntax.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <div className="max-w-6xl mx-auto space-y-4 p-4 sm:p-6 text-xs">
        <Link
          to={`/admin/courses/${courseId}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={13} />
          Back to Course
        </Link>
        <div>
          <h1 className="text-xl font-bold text-foreground">Manual Extraction &amp; Offline Ingestion</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate offline AI prompts and ingest materials for: {courseCode} · {courseName}
          </p>
        </div>
        {/* Error / Success Banners */}
        {errorMsg && (
          <div className="p-2.5 rounded-md bg-destructive/10 border border-destructive/20 text-destructive flex items-center gap-2 font-medium">
            <AlertCircle size={14} className="shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}
        {successNotice && (
          <div className="p-2.5 rounded-md bg-success/10 border border-success/20 text-success flex items-center gap-2 font-semibold">
            <CheckCircle2 size={14} className="shrink-0" />
            <span>{successNotice}</span>
          </div>
        )}

        {/* Course Banner (Auto-filled from where page is opened) */}
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-md bg-secondary/50 border border-border">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Course:</span>
            <span className="font-bold text-foreground text-xs">{courseCode} — {courseName}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              {courseType || "LANGUAGE"}
            </span>
          </div>
          <span className="text-xs font-medium text-muted-foreground">
            {activeProfileUsed}
          </span>
        </div>

        {/* Distinct Form Switcher */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 p-1 bg-secondary/50 rounded-lg border border-border">
          {([
            ["NOTES", "Notes only", BookOpen],
            ["QUIZ", "Questions only", BookOpen],
            ["BOTH", "Notes + Questions", BookOpen],
            ["PAST_EXAM", "Past exam", GraduationCap],
          ] as const).map(([mode, label, Icon]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setSelectedMode(mode);
                setTemplateData(null);
                setErrorMsg(null);
              }}
              className={`flex items-center justify-center gap-2 py-2 px-3 rounded-md text-xs font-bold transition cursor-pointer ${
                selectedMode === mode
                  ? "bg-background text-foreground shadow-xs border border-border/80"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              <Icon size={14} className={selectedMode === mode ? "text-primary" : ""} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* ----------------- FORM 1: MODULE QUIZ & NOTES ----------------- */}
        {formMode === "module" && (
          <div className="surface-sm rounded-md p-3.5 border border-border space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Target Module */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  Target Module <span className="text-destructive">*</span>
                </label>
                <select
                  value={selectedModuleId}
                  onChange={(e) => {
                    setSelectedModuleId(e.target.value);
                    setSelectedChapterId("");
                    setTemplateData(null);
                  }}
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground font-medium focus:ring-1 focus:ring-primary focus:outline-hidden"
                >
                  {modules.length === 0 ? (
                    <option value="">No modules found for this course</option>
                  ) : (
                    modules.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Target Chapter */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">Target Chapter (Optional)</label>
                <select
                  value={selectedChapterId}
                  onChange={(e) => {
                    setSelectedChapterId(e.target.value);
                    setTemplateData(null);
                  }}
                  disabled={availableChapters.length === 0}
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground font-medium focus:ring-1 focus:ring-primary focus:outline-hidden disabled:opacity-50"
                >
                  <option value="">
                    {availableChapters.length === 0 ? "Default / Entire Module" : "All Chapters / Default"}
                  </option>
                  {availableChapters.map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      Ch {ch.idx}: {ch.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mode Preference */}
            <div>
              <label className="block text-muted-foreground font-semibold mb-1.5">Module Content Mode</label>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { key: "quiz", label: "Quiz only", desc: "Question bank with distractors & explanations" },
                    { key: "notes", label: "Notes only", desc: "Structured markdown notes with key terms" },
                    { key: "both", label: "Both (Notes & Quiz)", desc: "Unified chapter notes + question bank bundle" },
                  ] as const
                ).map((p) => {
                  const isSelected = modulePreference === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        setSelectedMode(p.key === "notes" ? "NOTES" : p.key === "both" ? "BOTH" : "QUIZ");
                        setTemplateData(null);
                      }}
                      className={`p-2 rounded-md border text-left transition cursor-pointer ${
                        isSelected
                          ? "bg-primary/10 border-primary text-primary font-bold shadow-xs"
                          : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                    >
                      <p className="font-bold">{p.label}</p>
                      <p className="text-xs opacity-80 mt-0.5">{p.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Questions per difficulty (Default: 25) */}
            {modulePreference !== "notes" && (
              <div className="p-3 rounded-md bg-secondary/40 border border-border space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-foreground font-semibold">
                    Questions per Difficulty Level (Easy, Medium, Hard)
                  </label>
                  <span className="font-mono text-xs font-bold text-primary px-2 py-0.5 rounded bg-primary/10">
                    {(Number(questionsPerLevel) || 25) * 3} total questions
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={5}
                    max={100}
                    value={questionsPerLevel}
                    onChange={(e) => {
                      setQuestionsPerLevel(Math.max(1, Number(e.target.value) || 1));
                      setTemplateData(null);
                    }}
                    className="w-24 px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs font-semibold focus:ring-1 focus:ring-primary focus:outline-hidden"
                  />
                  <p className="text-xs text-muted-foreground">
                    By default, generates <strong>25 questions per difficulty</strong> (25 Easy + 25 Medium + 25 Hard = 75 total questions across all 3 tiers).
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----------------- FORM 2: PAST EXAM ----------------- */}
        {formMode === "past_exam" && (
          <div className="surface-sm rounded-md p-3.5 border border-border space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* University Selector */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  University <span className="text-destructive">*</span>
                </label>
                <select
                  value={selectedUniId}
                  onChange={(e) => {
                    setSelectedUniId(e.target.value);
                    setTemplateData(null);
                  }}
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground font-medium focus:ring-1 focus:ring-primary focus:outline-hidden"
                >
                  <option value="">Common / National Curriculum</option>
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.short_name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  Exam Year (e.g. 2016) <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={examYear}
                  onChange={(e) => {
                    setExamYear(e.target.value);
                    setTemplateData(null);
                  }}
                  placeholder="e.g. 2016"
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Exam Term (Midterm vs Final) */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  Exam Term <span className="text-destructive">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setExamTerm("mid");
                      setTemplateData(null);
                    }}
                    className={`py-1.5 px-3 rounded-md border text-center font-bold text-xs transition cursor-pointer ${
                      examTerm === "mid"
                        ? "bg-primary text-on-accent border-primary shadow-xs"
                        : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Midterm Exam
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExamTerm("final");
                      setTemplateData(null);
                    }}
                    className={`py-1.5 px-3 rounded-md border text-center font-bold text-xs transition cursor-pointer ${
                      examTerm === "final"
                        ? "bg-primary text-on-accent border-primary shadow-xs"
                        : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Final Exam
                  </button>
                </div>
              </div>

              {/* Exam Title (Auto-generated preview & customizable) */}
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  Exam Title (Auto-generated or custom)
                </label>
                <input
                  type="text"
                  value={examTitleCustom}
                  onChange={(e) => {
                    setExamTitleCustom(e.target.value);
                    setTemplateData(null);
                  }}
                  placeholder={computedExamTitle}
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                />
                <p className="text-xs text-muted-foreground mt-0.5">
                  Title: <span className="font-semibold text-foreground">{examTitleCustom.trim() || computedExamTitle}</span>
                </p>
              </div>
            </div>

            {/* Optional Existing Resource & Question Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-border/60">
              <div>
                <label className="block text-muted-foreground font-semibold mb-1">
                  Attach to Existing Resource (Optional)
                </label>
                <select
                  value={selectedExamResourceId}
                  onChange={(e) => {
                    setSelectedExamResourceId(e.target.value);
                    setTemplateData(null);
                  }}
                  className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground font-medium focus:ring-1 focus:ring-primary focus:outline-hidden"
                >
                  <option value="">-- Create New Past Exam Resource --</option>
                  {exams.map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      {ex.title} {ex.examYear ? `(${ex.examYear})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Question Range */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-muted-foreground font-semibold mb-1">From Q # (Opt)</label>
                  <input
                    type="number"
                    min={1}
                    value={fromQ}
                    onChange={(e) => {
                      setFromQ(e.target.value);
                      setTemplateData(null);
                    }}
                    placeholder="1"
                    className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                  />
                </div>
                <div>
                  <label className="block text-muted-foreground font-semibold mb-1">To Q # (Opt)</label>
                  <input
                    type="number"
                    min={1}
                    value={toQ}
                    onChange={(e) => {
                      setToQ(e.target.value);
                      setTemplateData(null);
                    }}
                    placeholder="50"
                    className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ----------------- STEP 1: PROMPT TEMPLATE GENERATION & DOWNLOAD ----------------- */}
        <div className="surface-sm rounded-md p-3.5 border border-border space-y-2.5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-primary text-on-accent inline-flex items-center justify-center text-xs">1</span>
                  Instruction Template & Source Excerpt
                </h4>
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  {templateData?.profileUsed || activeProfileUsed}
                </span>
              </div>
              <p className="text-muted-foreground text-xs mt-0.5">
                {formMode === "module"
                  ? "Generates chapter-scoped instructions for notes and 3-tier difficulty quiz."
                  : "Generates authentic past exam extraction prompt with university and term context."}
              </p>
            </div>

            <button
              type="button"
              onClick={handleFetchTemplate}
              disabled={fetchingTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-on-accent font-bold hover:opacity-90 transition shrink-0 cursor-pointer disabled:opacity-50"
            >
              {fetchingTemplate ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              <span>{templateData ? "Regenerate Template" : "Generate Template"}</span>
            </button>
          </div>

          {templateData && (
            <div className="pt-2 border-t border-border/70 space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs p-2 rounded bg-secondary/60">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground font-mono">
                    {templateData.charCount.toLocaleString()} characters
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className={templateData.hasSourceText ? "text-success font-medium" : "text-warning font-medium"}>
                    {templateData.hasSourceText ? "✓ Source text included" : "⚠ No plain text found; placeholder included"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopyTemplate}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground font-semibold border border-border transition cursor-pointer"
                  >
                    {templateCopied ? <Check size={11} className="text-success" /> : <Copy size={11} />}
                    <span>{templateCopied ? "Copied Prompt!" : "Copy Prompt"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground font-semibold border border-border transition cursor-pointer"
                  >
                    <Download size={11} />
                    <span>Download (.txt)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPreviewExpanded((prev) => !prev)}
                    className="text-primary hover:underline font-semibold ml-1 cursor-pointer"
                  >
                    {previewExpanded ? "Hide preview" : "View preview"}
                  </button>
                </div>
              </div>

              {previewExpanded && (
                <textarea
                  readOnly
                  rows={8}
                  value={templateData.template}
                  className="w-full p-3 bg-secondary/80 rounded-md border border-border font-mono text-xs leading-relaxed text-foreground select-all resize-none"
                />
              )}
            </div>
          )}
        </div>

        {/* ----------------- STEP 2: UPLOAD OR PASTE AI RESULT ----------------- */}
        <form onSubmit={handleIngestSubmit} className="surface-sm rounded-md p-3.5 border border-border space-y-3">
          <div>
            <h4 className="font-bold text-foreground flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-primary text-on-accent inline-flex items-center justify-center text-xs">2</span>
              Upload or Paste AI Output
            </h4>
            <p className="text-muted-foreground text-xs mt-0.5">
              The system will parse and store the result tagged with origin <strong>Manual</strong>.
            </p>
          </div>

          {/* Mode Switch: Paste vs File */}
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <button
              type="button"
              onClick={() => setIngestMode("paste")}
              className={`px-3 py-1 rounded-md text-xs font-bold transition cursor-pointer ${
                ingestMode === "paste"
                  ? "bg-primary text-on-accent"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              Paste text
            </button>
            <button
              type="button"
              onClick={() => setIngestMode("file")}
              className={`px-3 py-1 rounded-md text-xs font-bold transition cursor-pointer ${
                ingestMode === "file"
                  ? "bg-primary text-on-accent"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              Upload file (.json / .md / .txt)
            </button>
          </div>

          {ingestMode === "paste" ? (
            <div>
              <label className="block text-muted-foreground font-semibold mb-1">
                Paste JSON or Markdown Result
              </label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={7}
                placeholder={
                  formMode === "module" && modulePreference === "notes"
                    ? "Paste JSON object with 'markdown' or pure markdown study notes here..."
                    : "Paste JSON object containing 'questions': [...] here..."
                }
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-foreground font-mono text-xs focus:ring-1 focus:ring-primary focus:outline-hidden leading-relaxed"
              />
            </div>
          ) : (
            <div>
              <label className="block text-muted-foreground font-semibold mb-1">
                Select Output File (.json, .txt, .md)
              </label>
              <input
                type="file"
                accept=".json,.txt,.md"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-foreground text-xs file:mr-3 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-primary file:text-on-accent cursor-pointer"
              />
            </div>
          )}

          {/* Validation Feedback Panel */}
          {validation && (
            <div className="space-y-2 pt-1">
              {validation.reviewFlags.length > 0 && (
                <div className="p-3 rounded-md bg-warning/10 border border-warning/30 text-warning-foreground text-xs space-y-1.5">
                  <div className="font-bold flex items-center gap-1.5 text-warning">
                    <AlertTriangle size={14} className="shrink-0" />
                    <span>Needs your review ({validation.reviewFlags.length} flagged)</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {validation.reviewFlags.map((flag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-warning/20 text-warning border border-warning/30"
                      >
                        {flag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!validation.valid && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-xs space-y-1.5">
                  <div className="font-bold flex items-center gap-1.5">
                    <AlertCircle size={14} className="shrink-0" />
                    <span>Validation errors ({validation.errors.length}):</span>
                  </div>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto admin-scroll-y space-y-0.5 text-xs font-mono leading-relaxed">
                    {validation.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validation.warnings.length > 0 && (
                <div className="p-2.5 rounded-md bg-secondary/80 border border-border text-xs space-y-1">
                  <div className="font-semibold text-muted-foreground flex items-center gap-1.5">
                    <AlertCircle size={13} className="text-warning shrink-0" />
                    <span>Warnings ({validation.warnings.length} non-blocking notices):</span>
                  </div>
                  <ul className="list-disc list-inside max-h-24 overflow-y-auto admin-scroll-y space-y-0.5 text-xs text-muted-foreground font-mono">
                    {validation.warnings.map((warn, i) => (
                      <li key={i}>{warn}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validation.valid && (
                <div className="p-2 rounded-md bg-success/10 border border-success/20 text-success text-xs flex items-center justify-between font-semibold">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} />
                    <span>Valid format: {validation.questionsCount} questions parsed successfully</span>
                  </span>
                  <span className="text-xs opacity-80">Ready to ingest</span>
                </div>
              )}
            </div>
          )}

          {/* Optional Title & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-muted-foreground font-semibold mb-1">Item Title (Optional)</label>
              <input
                type="text"
                value={customItemTitle}
                onChange={(e) => setCustomItemTitle(e.target.value)}
                placeholder="Leave blank for automatic title"
                className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
              />
            </div>

            <div>
              <label className="block text-muted-foreground font-semibold mb-1">Save State</label>
              <select
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value as "draft" | "active")}
                className="w-full px-2.5 py-1.5 rounded-md bg-background border border-border text-foreground text-xs font-medium focus:ring-1 focus:ring-primary focus:outline-hidden"
              >
                <option value="draft">Save as Draft (Review tab)</option>
                <option value="active">Active (Publish immediately)</option>
              </select>
            </div>
          </div>

          {/* Footer Submit */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground font-medium">
              Source marked as <span className="font-bold text-foreground">[Manual]</span>
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onClose?.()}
                disabled={submitting}
                className="px-3 py-1.5 rounded-md bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80 cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || (currentPreference !== "notes" && !!(validation && !validation.valid))}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 transition cursor-pointer disabled:opacity-50 shadow-xs"
              >
                {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                <span>{submitting ? "Ingesting…" : "Ingest Material"}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
  );
}
